/**
 * Offices (geofence centres) and shift policies.
 *
 * These two are on one screen because they only make sense together: a
 * geofenced policy is meaningless without an office to measure from, and an
 * office does nothing until a policy points at it.
 *
 * Managers reach this screen — reading offices and policies only needs a
 * signed-in user — but every write here requires company_admin. The add
 * controls are therefore hidden from a manager rather than shown and left to
 * fail on submit, matching how CompaniesPage hides its create button.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Loader2, MapPin, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import { captureLocation } from '../../lib/geolocation';
import { isCompanyAdmin, useAuth } from '../../store/auth';
import type { Office, Page, ShiftPolicy } from '../../types';


const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function WorkplacesPage() {
  const queryClient = useQueryClient();
  const me = useAuth((s) => s.user);
  const canEdit = isCompanyAdmin(me);
  const [showOffice, setShowOffice] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  const { data: offices } = useQuery<Page<Office>>({
    queryKey: ['admin-offices'],
    queryFn: async () => (await client.get('/api/v1/admin/offices')).data,
  });

  const { data: policies } = useQuery<Page<ShiftPolicy>>({
    queryKey: ['admin-shift-policies'],
    queryFn: async () => (await client.get('/api/v1/admin/shift-policies')).data,
  });

  const createOffice = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/admin/offices', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-offices'] });
      setShowOffice(false);
      toast.success('Office added');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const createPolicy = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/admin/shift-policies', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shift-policies'] });
      setShowPolicy(false);
      toast.success('Shift policy added');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="space-y-8">
      {/* --- Offices --- */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Offices (geofence centres)</h2>
          {canEdit && (
            <button type="button" className="btn-secondary" onClick={() => setShowOffice((v) => !v)}>
              <Plus className="h-4 w-4" />
              {showOffice ? 'Cancel' : 'Add office'}
            </button>
          )}
        </div>

        {canEdit && showOffice && (
          <OfficeForm submitting={createOffice.isPending} onSubmit={createOffice.mutate} />
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {offices?.items.map((office) => (
            <div key={office.id} className="card">
              <h3 className="flex items-center gap-2 font-medium text-gray-900">
                <MapPin className="h-4 w-4 text-indigo-600" />
                {office.name}
              </h3>
              <p className="mt-1 font-mono text-xs text-gray-500">
                {office.latitude.toFixed(5)}, {office.longitude.toFixed(5)}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Radius <strong>{office.radiusM} m</strong>
              </p>
              <a
                className="mt-2 inline-block text-xs text-indigo-600 hover:underline"
                href={`https://www.google.com/maps?q=${office.latitude},${office.longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                Verify on a map ↗
              </a>
            </div>
          ))}
          {offices?.items.length === 0 && (
            <p className="text-sm text-gray-500">
              No offices yet. Add one before creating a geofenced shift policy.
            </p>
          )}
        </div>
      </section>

      {/* --- Shift policies --- */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Shift policies</h2>
          {canEdit && (
            <button type="button" className="btn-secondary" onClick={() => setShowPolicy((v) => !v)}>
              <Plus className="h-4 w-4" />
              {showPolicy ? 'Cancel' : 'Add policy'}
            </button>
          )}
        </div>

        {canEdit && showPolicy && (
          <PolicyForm
            offices={offices?.items ?? []}
            submitting={createPolicy.isPending}
            onSubmit={createPolicy.mutate}
          />
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {policies?.items.map((policy) => (
            <div key={policy.id} className="card">
              <h3 className="flex items-center gap-2 font-medium text-gray-900">
                <Clock className="h-4 w-4 text-indigo-600" />
                {policy.name}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                {policy.startTime.slice(0, 5)}–{policy.endTime.slice(0, 5)} ·{' '}
                {policy.workingDays
                  .split(',')
                  .map((d) => DAY_NAMES[Number(d)])
                  .join(' ')}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="badge bg-gray-100 text-gray-600">
                  every {policy.pingIntervalMinutes}m
                </span>
                <span className="badge bg-gray-100 text-gray-600">
                  ±{policy.pingJitterMinutes}m jitter
                </span>
                <span
                  className={`badge ${
                    policy.geofenceRequired
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {policy.geofenceRequired ? 'office-gated' : 'field role'}
                </span>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Present at {Math.round(policy.fullDayThreshold * 100)}%, half-day at{' '}
                {Math.round(policy.halfDayThreshold * 100)}%
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function OfficeForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radiusM: '150',
  });
  const [locating, setLocating] = useState(false);

  return (
    <form
      className="card grid gap-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name: form.name.trim(),
          address: form.address.trim() || null,
          latitude: Number(form.latitude),
          longitude: Number(form.longitude),
          radiusM: Number(form.radiusM),
        });
      }}
    >
      <div>
        <label className="label" htmlFor="o-name">Office name</label>
        <input
          spellCheck={false}
          id="o-name"
          className="input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </div>
      <div>
        <label className="label" htmlFor="o-radius">Radius (metres)</label>
        <input
          id="o-radius"
          type="number"
          min={25}
          max={5000}
          className="input"
          value={form.radiusM}
          onChange={(e) => setForm({ ...form, radiusM: e.target.value })}
          required
        />
        <p className="mt-1 text-xs text-gray-500">
          Below ~50 m, ordinary GPS error makes staff flicker in and out.
        </p>
      </div>
      <div>
        <label className="label" htmlFor="o-lat">Latitude</label>
        <input
          spellCheck={false}
          id="o-lat"
          className="input font-mono"
          value={form.latitude}
          onChange={(e) => setForm({ ...form, latitude: e.target.value })}
          required
        />
      </div>
      <div>
        <label className="label" htmlFor="o-lng">Longitude</label>
        <input
          spellCheck={false}
          id="o-lng"
          className="input font-mono"
          value={form.longitude}
          onChange={(e) => setForm({ ...form, longitude: e.target.value })}
          required
        />
      </div>

      <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Add office
        </button>
        {/* Standing in the office is the most reliable way to get the centre
            right; typing coordinates by hand is error-prone. */}
        <button
          type="button"
          className="btn-secondary"
          disabled={locating}
          onClick={async () => {
            setLocating(true);
            const geo = await captureLocation();
            setLocating(false);
            if (geo.ok) {
              setForm((f) => ({
                ...f,
                latitude: geo.location.latitude.toFixed(6),
                longitude: geo.location.longitude.toFixed(6),
              }));
              toast.success(`Captured (±${Math.round(geo.location.accuracyM)} m)`);
            } else {
              toast.error(geo.message);
            }
          }}
        >
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
          Use my current location
        </button>
      </div>
    </form>
  );
}

function PolicyForm({
  offices,
  submitting,
  onSubmit,
}: {
  offices: Office[];
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    // What a new policy starts as. Only a starting point — every field
    // here is editable before saving and afterwards, and no existing
    // policy is affected by changing this.
    startTime: '08:30',
    endTime: '17:30',
    workingDays: ['1', '2', '3', '4', '5', '6'],
    pingIntervalMinutes: 10,
    pingGraceMinutes: 4,
    pingJitterMinutes: 5,
    geofenceRequired: true,
    officeId: '',
  });

  const toggleDay = (d: string) =>
    setForm((f) => ({
      ...f,
      workingDays: f.workingDays.includes(d)
        ? f.workingDays.filter((x) => x !== d)
        : [...f.workingDays, d].sort(),
    }));

  return (
    <form
      className="card grid gap-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name: form.name.trim(),
          startTime: `${form.startTime}:00`,
          endTime: `${form.endTime}:00`,
          workingDays: form.workingDays.join(','),
          pingIntervalMinutes: form.pingIntervalMinutes,
          pingGraceMinutes: form.pingGraceMinutes,
          pingJitterMinutes: form.pingJitterMinutes,
          geofenceRequired: form.geofenceRequired,
          officeId: form.officeId || null,
        });
      }}
    >
      <div>
        <label className="label" htmlFor="p-name">Policy name</label>
        <input
          spellCheck={false}
          id="p-name"
          className="input"
          placeholder="Office Staff 8:30–5:30"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label" htmlFor="p-start">Start</label>
          <input
            id="p-start"
            type="time"
            className="input"
            value={form.startTime}
            onChange={(e) => setForm({ ...form, startTime: e.target.value })}
          />
        </div>
        <div>
          <label className="label" htmlFor="p-end">End</label>
          <input
            id="p-end"
            type="time"
            className="input"
            value={form.endTime}
            onChange={(e) => setForm({ ...form, endTime: e.target.value })}
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <span className="label">Working days</span>
        <div className="flex flex-wrap gap-1.5">
          {DAY_NAMES.slice(1).map((label, i) => {
            const value = String(i + 1);
            const on = form.workingDays.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleDay(value)}
                className={`rounded-lg px-3 py-1.5 text-sm ring-1 ${
                  on
                    ? 'bg-indigo-600 text-white ring-indigo-600'
                    : 'bg-white text-gray-600 ring-gray-300'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="label" htmlFor="p-interval">Check-in every (minutes)</label>
        <input
          id="p-interval"
          type="number"
          min={5}
          max={240}
          className="input"
          value={form.pingIntervalMinutes}
          onChange={(e) =>
            setForm({ ...form, pingIntervalMinutes: Number(e.target.value) })
          }
        />
      </div>
      <div>
        <label className="label" htmlFor="p-jitter">Random jitter (minutes)</label>
        <input
          id="p-jitter"
          type="number"
          min={0}
          max={form.pingIntervalMinutes - form.pingGraceMinutes}
          className="input"
          value={form.pingJitterMinutes}
          onChange={(e) => setForm({ ...form, pingJitterMinutes: Number(e.target.value) })}
        />
        <p className="mt-1 text-xs text-gray-500">
          Must leave room for the grace period ({form.pingGraceMinutes} min)
          inside the interval. Without jitter, the prompt time is predictable
          and easy to game.
        </p>
      </div>

      <div className="sm:col-span-2 space-y-2">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={form.geofenceRequired}
            onChange={(e) => setForm({ ...form, geofenceRequired: e.target.checked })}
          />
          Require staff to be at the office
        </label>
        <p className="text-xs text-gray-500">
          Leave this off for field roles. Their location is still recorded — they
          just are not marked absent for being at a client site.
        </p>

        {form.geofenceRequired && (
          <div>
            <label className="label" htmlFor="p-office">Office</label>
            <select
              id="p-office"
              className="input"
              value={form.officeId}
              onChange={(e) => setForm({ ...form, officeId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {offices.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="sm:col-span-2">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Create policy
        </button>
      </div>
    </form>
  );
}
